/** @typedef {import('@/types').Id} Id */
/** @typedef {import('@/types').Item} Item */

import { Storage } from '@/persistence/Repository'
import { Local } from '@/persistence/Local'
import { Large } from '@/persistence/Large'
import { Cloud } from '@/persistence/Cloud'
import { Folder } from '@/persistence/Folder'
import { Paged } from '@/persistence/Paged'
import { current_user, me } from '@/utils/serverless'
import { as_type, as_created_at, as_query_id, is_itemid } from '@/utils/itemid'
import { get } from 'idb-keyval'
import { profile_sync_log } from '@/utils/profile-sync-log'
import { valid_name } from '@/utils/valid-name'

export { Storage }
export { History } from '@/persistence/History'

/**
 * @extends {Storage}
 */
export class Poster extends Folder(Large(Cloud(Storage))) {}

/**
 * @extends {Storage}
 */
export class Cutout extends Folder(Large(Cloud(Storage))) {}

/**
 * @extends {Storage}
 */
export class Shadow extends Folder(Large(Cloud(Storage))) {}

/**
 * @extends {Storage}
 */
export class Me extends Cloud(Local(Storage)) {
  constructor() {
    super(localStorage.me)
  }

  async save(
    items = document.querySelector(`[itemid="${this.id}"]`) ?? undefined
  ) {
    if (!current_user.value) return
    const me_val =
      /** @type {{name?: string, visited?: string, avatar?: string} | null | undefined} */ (
        me.value
      )
    if (!me_val) return
    const name_ok = valid_name(me_val.name)
    if (!me_val.visited) me_val.visited = new Date().toISOString()

    let save_items = items
    if (!name_ok && items?.cloneNode) {
      save_items = /** @type {Element} */ (items.cloneNode(true))
      const name_el = save_items.querySelector('[itemprop="name"]')
      if (name_el) {
        const cached = localStorage.getItem(this.id)
        let prev_name = ''
        if (cached) {
          const temp = document.createElement('div')
          temp.innerHTML = cached
          prev_name =
            temp.querySelector('[itemprop="name"]')?.textContent?.trim() || ''
        }
        name_el.textContent = valid_name(prev_name) ? prev_name : ''
      }
    }

    if (!name_ok)
      profile_sync_log('me_save_persist_without_display_name', {
        itemid: this.id,
        visited: me_val.visited
      })
    profile_sync_log('me_save_persist', {
      itemid: this.id,
      name: me_val.name,
      avatar: me_val.avatar,
      visited: me_val.visited,
      name_ok
    })
    await super.save(save_items ?? undefined)
  }
}

/** @extends {Storage} */
export class Relation extends Cloud(Local(Storage)) {
  constructor() {
    super(/** @type {Id} */ (`${localStorage.me}/relations`))
  }
}

/** @extends {Storage} */
export class Thought extends Paged(Cloud(Local(Storage))) {
  constructor() {
    super(/** @type {Id} */ (`${localStorage.me}/thoughts`))
  }
}

/** @extends {Storage} - Per-statement storage. Saves individual statement divs by itemid. */
export class Statements extends Folder(Paged(Cloud(Local(Storage)))) {
  constructor() {
    super(/** @type {Id} */ (`${localStorage.me}/statements`))
  }
}

/** @extends {Storage} */
export class Offline extends Cloud(Storage) {
  async save() {
    let { id } = this
    const outer_html = await get(id)
    if (!outer_html) return

    if (id.startsWith('/+/'))
      id = /** @type {Id} */ (
        `${localStorage.me}/${as_type(id)}/${as_created_at(id)}`
      )
    if (!is_itemid(id)) {
      console.error('invalid itemid', id)
      return
    }

    const temp_container = document.createElement('div')
    temp_container.innerHTML = outer_html

    const content = temp_container.firstElementChild
    if (content) {
      content.setAttribute('itemid', id)
      content.id = as_query_id(id)
    }

    this.id = id
    await super.save({ outerHTML: temp_container.innerHTML })
  }
}
